import React from "react";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./InstitutionSolutionsPage.module.scss";

const FEATURES = [
    {
        key: "muni_feat_dashboard",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
            </svg>
        ),
    },
    {
        key: "muni_feat_institutions",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
    },
    {
        key: "muni_feat_greensteps",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
        ),
    },
    {
        key: "muni_feat_operations",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
        ),
    },
    {
        key: "muni_feat_waste",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
        ),
    },
    {
        key: "muni_feat_carbon",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                <path d="M2 12h20" />
            </svg>
        ),
    },
];

const EXTRAS = [
    {
        key: "muni_extra_goals",
        icon: (
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
            </svg>
        ),
    },
    {
        key: "muni_extra_school",
        icon: (
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
        ),
    },
    {
        key: "muni_extra_supply",
        icon: (
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34" />
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
            </svg>
        ),
    },
    {
        key: "muni_extra_assets",
        icon: (
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
        ),
    },
];

export default function InstitutionSolutionsPage() {
    const { locale } = useRouter();
    const t = useTranslations("CompanySolutionsPage");

    const [heroRef, heroInView] = useInView();
    const [featuresRef, featuresInView] = useInView();
    const [showcase1Ref, showcase1InView] = useInView();
    const [showcase2Ref, showcase2InView] = useInView();
    const [assessmentRef, assessmentInView] = useInView();
    const [extrasRef, extrasInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">
                    {"Hugbúnaðurinn - " + WEBPAGE_TITLE}
                </title>
                <meta name="keywords" content="Hugbúnaðurinn sveitarfélög" />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Sveitarfélög`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("municipality_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("municipality_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("muni_badge")}</span>
                        <h1 className={styles.heroTitle}>
                            {t("muni_hero_title")}
                        </h1>
                        <p className={styles.heroSubtitle}>
                            {t("muni_hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>
                            {t("municipality_desc")}
                        </p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <section
                    ref={featuresRef}
                    className={`${styles.features} ${styles.revealTarget} ${featuresInView ? styles.revealed : ""}`}
                >
                    {FEATURES.map((feat) => (
                        <div key={feat.key} className={styles.featureCard}>
                            <div className={styles.featureIcon}>
                                {feat.icon}
                            </div>
                            <h3 className={styles.featureTitle}>
                                {t(`${feat.key}_title`)}
                            </h3>
                            <p className={styles.featureDesc}>
                                {t(`${feat.key}_desc`)}
                            </p>
                        </div>
                    ))}
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={showcase1Ref}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${showcase1InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("muni_showcase_dashboard_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("dashboard_for_municipalities")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("laufin_municipalities_access_dashboard")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>4</span>
                                    <span className={styles.statLabel}>
                                        {t("muni_stat_views")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("muni_stat_views_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("muni_stat_institutions")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("muni_stat_institutions_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardWide}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("muni_stat_realtime")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("muni_stat_realtime_desc")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={showcase2Ref}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${showcase2InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("muni_showcase_steps_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("over_100_action_oriented_steps")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("leafs_msg")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>
                                        120+
                                    </span>
                                    <span className={styles.statLabel}>
                                        {t("muni_stat_actions")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <span className={styles.statNumber}>3</span>
                                    <span className={styles.statLabel}>
                                        {t("muni_stat_categories")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("muni_stat_categories_esg")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardWide}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <circle cx="12" cy="12" r="6" />
                                            <circle cx="12" cy="12" r="2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("muni_stat_goals")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={assessmentRef}
                    className={`${styles.assessment} ${styles.revealTarget} ${assessmentInView ? styles.revealed : ""}`}
                >
                    <div className={styles.assessmentInner}>
                        <span className={styles.showcaseLabel}>
                            {t("status_assessment")}
                        </span>
                        <h2 className={styles.assessmentTitle}>
                            {t("muni_assessment_title")}
                        </h2>
                        <div className={styles.assessmentSteps}>
                            <div className={styles.assessmentStep}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.featureIcon}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                </div>
                                <p className={styles.stepText}>
                                    {t("measuring_environmental")}
                                </p>
                            </div>
                            <div className={styles.stepDivider}>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>
                            <div className={styles.assessmentStep}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.featureIcon}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <line x1="18" y1="20" x2="18" y2="10" />
                                        <line x1="12" y1="20" x2="12" y2="4" />
                                        <line x1="6" y1="20" x2="6" y2="14" />
                                    </svg>
                                </div>
                                <p className={styles.stepText}>
                                    {t("results_set")}
                                </p>
                            </div>
                            <div className={styles.stepDivider}>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>
                            <div className={styles.assessmentStep}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.featureIcon}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                </div>
                                <p className={styles.stepText}>
                                    {t("results_announced")}
                                </p>
                            </div>
                        </div>
                        <a
                            className={styles.ctaBtn}
                            href="/sveitarfelog/stodumat"
                        >
                            {t("further_information_btn")}
                        </a>
                    </div>
                </section>

                <section
                    ref={extrasRef}
                    className={`${styles.extras} ${styles.revealTarget} ${extrasInView ? styles.revealed : ""}`}
                >
                    <h2 className={styles.extrasTitle}>
                        {t("muni_extras_title")}
                    </h2>
                    <div className={styles.extrasGrid}>
                        {EXTRAS.map((extra) => (
                            <div key={extra.key} className={styles.extraCard}>
                                <div className={styles.extraIcon}>
                                    {extra.icon}
                                </div>
                                <h4 className={styles.extraName}>
                                    {t(`${extra.key}_title`)}
                                </h4>
                                <p className={styles.extraDesc}>
                                    {t(`${extra.key}_desc`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("better_coordination_sustainable_development")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("promoting_sustainable_development")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
